/**
 * ReceivePartsModal
 *
 * Opens when user clicks the Receive button on a BOM line.
 * Shows all open PO lines for that part (one receive form per PO line)
 * plus a Stock / No-PO section at the bottom.
 *
 * Props:
 *   line        — JobBomLine object from the BOM tab
 *   onClose     — called when modal should close
 *   onReceived  — called after any successful receive (triggers BOM reload)
 */

import { useEffect, useState } from 'react';
import { fetchPoLinesForBomLine, receiveLine, receiveStock } from '../../../api/purchasing';
import { LoadingSpinner } from '../../common';

function fmtQty(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(4).replace(/\.?0+$/, '');
}

function fmtDate(d) {
  if (!d) return null;
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
}

const STATUS_SKIP = ['received', 'cancelled'];

export default function ReceivePartsModal({ line, onClose, onReceived }) {
  const [poLines, setPoLines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Per-PO-line: qty input + notes + saving state + error
  const [poInputs, setPoInputs] = useState({});   // { [poLineId]: { qty, notes } }
  const [poSaving, setPoSaving] = useState({});    // { [poLineId]: bool }
  const [poErrors, setPoErrors] = useState({});    // { [poLineId]: string }
  const [poSuccess, setPoSuccess] = useState({});  // { [poLineId]: bool }

  // Stock receive
  const [stockQty, setStockQty] = useState('');
  const [stockNotes, setStockNotes] = useState('');
  const [stockSaving, setStockSaving] = useState(false);
  const [stockError, setStockError] = useState('');
  const [stockSuccess, setStockSuccess] = useState(false);

  // Track qty received during this modal session (line prop won't update live)
  const [sessionReceived, setSessionReceived] = useState(0);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const all = await fetchPoLinesForBomLine(line.id);
        // Only show lines that haven't been fully received/cancelled
        const open = all.filter(l => !STATUS_SKIP.includes(l.line_status));
        setPoLines(open);
        // Pre-fill qty inputs with remaining on each PO line
        const inputs = {};
        open.forEach(pl => {
          const remaining = Math.max(parseFloat(pl.qty_ordered) - parseFloat(pl.qty_received), 0);
          inputs[pl.id] = { qty: fmtQty(remaining), notes: '' };
        });
        setPoInputs(inputs);
      } catch {
        setPoLines([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [line.id]);

  function setPoInput(id, field, val) {
    setPoInputs(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  }

  async function handleReceivePo(poLine) {
    const input = poInputs[poLine.id] || {};
    const qty = parseFloat(input.qty);
    if (!qty || qty <= 0) {
      setPoErrors(prev => ({ ...prev, [poLine.id]: 'Enter a quantity greater than 0.' }));
      return;
    }
    setPoSaving(prev => ({ ...prev, [poLine.id]: true }));
    setPoErrors(prev => ({ ...prev, [poLine.id]: '' }));
    try {
      await receiveLine(poLine.id, { qty_received: qty, notes: input.notes || '' });
      const newSessionTotal = sessionReceived + qty;
      setSessionReceived(newSessionTotal);
      setPoSuccess(prev => ({ ...prev, [poLine.id]: true }));
      onReceived?.();
      // Auto-close if all parts now received
      const newRemaining = Math.max(qtyRequired - ((parseFloat(line.qty_received) || 0) + newSessionTotal), 0);
      if (newRemaining <= 0) {
        setTimeout(() => onClose(), 800);
        return;
      }
      // Reload PO lines to reflect updated state
      const all = await fetchPoLinesForBomLine(line.id);
      const open = all.filter(l => !STATUS_SKIP.includes(l.line_status));
      setPoLines(open);
    } catch (err) {
      setPoErrors(prev => ({
        ...prev,
        [poLine.id]: err.response?.data?.error || 'Receive failed.',
      }));
    } finally {
      setPoSaving(prev => ({ ...prev, [poLine.id]: false }));
    }
  }

  async function handleReceiveStock() {
    const qty = parseFloat(stockQty);
    if (!qty || qty <= 0) {
      setStockError('Enter a quantity greater than 0.');
      return;
    }
    setStockSaving(true);
    setStockError('');
    try {
      await receiveStock(line.id, { qty_received: qty, notes: stockNotes });
      const newSessionTotal = sessionReceived + qty;
      setSessionReceived(newSessionTotal);
      setStockSuccess(true);
      setStockQty('');
      setStockNotes('');
      onReceived?.();
      // Auto-close if all parts now received
      const newRemaining = Math.max(qtyRequired - ((parseFloat(line.qty_received) || 0) + newSessionTotal), 0);
      if (newRemaining <= 0) {
        setTimeout(() => onClose(), 800);
        return;
      }
    } catch (err) {
      setStockError(err.response?.data?.error || 'Receive failed.');
    } finally {
      setStockSaving(false);
    }
  }

  const qtyRequired = parseFloat(line.qty_required) || 0;
  const qtyReceived = (parseFloat(line.qty_received) || 0) + sessionReceived;
  const qtyRemaining = Math.max(qtyRequired - qtyReceived, 0);
  const isFullyReceived = qtyRemaining <= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Receive Parts</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-800">{line.part_display}</span>
              {line.part_description && (
                <span className="ml-1 text-gray-400">— {line.part_description}</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
        </div>

        {/* Summary bar */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex gap-6 text-sm">
          <div>
            <span className="text-gray-500">Required: </span>
            <span className="font-semibold text-gray-800">{fmtQty(qtyRequired)}</span>
          </div>
          <div>
            <span className="text-gray-500">Received: </span>
            <span className="font-semibold text-gray-800">{fmtQty(qtyReceived)}</span>
          </div>
          <div>
            <span className="text-gray-500">Remaining: </span>
            <span className={`font-semibold ${qtyRemaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {fmtQty(qtyRemaining)}
            </span>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-4"><LoadingSpinner /></div>
          ) : (
            <>
              {/* PO Lines section */}
              {poLines.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Open Purchase Orders
                  </h3>
                  <div className="space-y-3">
                    {poLines.map(pl => {
                      const poRemaining = Math.max(
                        parseFloat(pl.qty_ordered) - parseFloat(pl.qty_received), 0
                      );
                      const input = poInputs[pl.id] || { qty: '', notes: '' };
                      const saving = poSaving[pl.id];
                      const error = poErrors[pl.id];
                      const success = poSuccess[pl.id];

                      return (
                        <div key={pl.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                          {/* Vendor / PO info */}
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-gray-800 text-sm">{pl.vendor_name || 'Vendor'}</span>
                              {pl.po_number && (
                                <span className="ml-2 text-xs text-gray-400">PO {pl.po_number}</span>
                              )}
                            </div>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              pl.line_status === 'partial_received'
                                ? 'bg-yellow-100 text-yellow-700'
                                : pl.line_status === 'confirmed'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {pl.line_status.replace(/_/g, ' ')}
                            </span>
                          </div>

                          {/* Qty summary */}
                          <div className="flex gap-4 text-xs text-gray-500 mb-3">
                            <span>Ordered: <strong className="text-gray-700">{fmtQty(pl.qty_ordered)}</strong></span>
                            <span>Received: <strong className="text-gray-700">{fmtQty(pl.qty_received)}</strong></span>
                            <span>Remaining: <strong className="text-red-600">{fmtQty(poRemaining)}</strong></span>
                            {pl.expected_delivery_date && (
                              <span>Expected: <strong className="text-gray-700">{fmtDate(pl.expected_delivery_date)}</strong></span>
                            )}
                          </div>

                          {success ? (
                            <p className="text-green-600 text-sm font-medium">✓ Received successfully</p>
                          ) : isFullyReceived ? (
                            <p className="text-green-600 text-sm font-medium">✓ All required qty received</p>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-500 mb-1">Qty to receive (max {fmtQty(Math.min(poRemaining, qtyRemaining))})</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max={Math.min(poRemaining, qtyRemaining)}
                                    step="any"
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    value={input.qty}
                                    onChange={e => {
                                      let val = parseFloat(e.target.value);
                                      const cap = Math.min(poRemaining, qtyRemaining);
                                      if (val > cap) val = cap;
                                      setPoInput(pl.id, 'qty', val > 0 ? String(val) : e.target.value);
                                    }}
                                    onFocus={e => e.target.select()}
                                    disabled={saving}
                                  />
                                </div>
                                <div className="flex-1">
                                  <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                                  <input
                                    type="text"
                                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g. Packing slip #123"
                                    value={input.notes}
                                    onChange={e => setPoInput(pl.id, 'notes', e.target.value)}
                                    disabled={saving}
                                  />
                                </div>
                                <button
                                  onClick={() => handleReceivePo(pl)}
                                  disabled={saving}
                                  className="shrink-0 px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                                >
                                  {saving ? '…' : 'Receive'}
                                </button>
                              </div>
                              {error && <p className="text-red-600 text-xs">{error}</p>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reverse / Undo section — only show when parts have been received */}
              {qtyReceived > 0 && (
                <ReverseSection
                  line={line}
                  qtyReceived={qtyReceived}
                  poLines={poLines}
                  onReversed={() => { setSessionReceived(0); onReceived?.(); }}
                />
              )}

              {/* Stock section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Stock / No Purchase Order
                </h3>
                <div className="border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-3">
                    Use this to receive qty pulled from existing stock with no associated PO.
                  </p>
                  {stockSuccess && (
                    <p className="text-green-600 text-sm font-medium mb-2">✓ Stock receipt recorded</p>
                  )}
                  {isFullyReceived ? (
                    <p className="text-green-600 text-sm font-medium">✓ All required qty received — no stock receive needed</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2 items-end">
                        <div className="w-32">
                          <label className="block text-xs text-gray-500 mb-1">Qty from stock (max {fmtQty(qtyRemaining)})</label>
                          <input
                            type="number"
                            min="0"
                            max={qtyRemaining}
                            step="any"
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            value={stockQty}
                            onChange={e => {
                              let val = parseFloat(e.target.value);
                              if (val > qtyRemaining) val = qtyRemaining;
                              setStockQty(val > 0 ? String(val) : e.target.value);
                              setStockSuccess(false);
                            }}
                            onFocus={e => e.target.select()}
                            disabled={stockSaving}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g. From bin A-12"
                            value={stockNotes}
                            onChange={e => setStockNotes(e.target.value)}
                            disabled={stockSaving}
                          />
                        </div>
                        <button
                          onClick={handleReceiveStock}
                          disabled={stockSaving}
                          className="shrink-0 px-4 py-1.5 rounded-md bg-gray-700 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                        >
                          {stockSaving ? '…' : 'Receive'}
                        </button>
                      </div>
                      {stockError && <p className="text-red-600 text-xs">{stockError}</p>}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * ReverseSection — collapsible section for undoing accidental receives.
 * Sends negative qty to the same receive endpoints.
 */
function ReverseSection({ line, qtyReceived, poLines, onReversed }) {
  const [open, setOpen] = useState(false);
  const [reverseQty, setReverseQty] = useState('');
  const [reverseNotes, setReverseNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleReverseStock() {
    const qty = parseFloat(reverseQty);
    if (!qty || qty <= 0) { setError('Enter a positive quantity to reverse.'); return; }
    if (qty > qtyReceived) { setError(`Cannot reverse more than ${fmtQty(qtyReceived)} received.`); return; }
    setSaving(true);
    setError('');
    try {
      await receiveStock(line.id, { qty_received: -qty, notes: reverseNotes || 'Reversal' });
      setSuccess(true);
      setReverseQty('');
      setReverseNotes('');
      onReversed?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Reverse failed.');
    } finally { setSaving(false); }
  }

  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium"
      >
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Reverse / Undo Receive
      </button>
      {open && (
        <div className="mt-2 border border-red-200 rounded-lg p-3 bg-red-50">
          <p className="text-xs text-red-600 mb-3">
            Use this to undo an accidental receive. Enter the quantity to reverse.
            The part status will update automatically.
          </p>
          {success && <p className="text-green-600 text-sm font-medium mb-2">✓ Reversal applied</p>}
          <div className="flex gap-2 items-end">
            <div className="w-28">
              <label className="block text-xs text-red-600 mb-1">Qty to reverse (max {fmtQty(qtyReceived)})</label>
              <input
                type="number"
                min="0"
                max={qtyReceived}
                step="any"
                className="w-full border border-red-300 rounded-md px-2 py-1.5 text-sm bg-white focus:ring-1 focus:ring-red-500 focus:border-red-500"
                value={reverseQty}
                onChange={e => { setReverseQty(e.target.value); setSuccess(false); }}
                onFocus={e => e.target.select()}
                disabled={saving}
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-red-600 mb-1">Reason</label>
              <input
                type="text"
                className="w-full border border-red-300 rounded-md px-2 py-1.5 text-sm bg-white focus:ring-1 focus:ring-red-500 focus:border-red-500"
                placeholder="e.g. Wrong part received"
                value={reverseNotes}
                onChange={e => setReverseNotes(e.target.value)}
                disabled={saving}
              />
            </div>
            <button
              onClick={handleReverseStock}
              disabled={saving}
              className="shrink-0 px-4 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? '…' : 'Reverse'}
            </button>
          </div>
          {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
        </div>
      )}
    </div>
  );
}
